import {
  decimalDifference,
  decimalDivide,
  decimalMultiply,
  decimalRoundingToNumber,
} from '@shared/utils/floating-math';

import { ProtocolEntryGridRow } from '../types/protocol-entry-grid-row';

export const isTargetDateDaysOutValidForDiscontinuation = (row: ProtocolEntryGridRow): boolean => {
  const targetDateDaysOut = Number(row?.target_date_days_out) || 0;

  return targetDateDaysOut !== 0;
};

export const canDiscontinuationExist = (row: ProtocolEntryGridRow): boolean => {
  return !!(isTargetDateDaysOutValidForDiscontinuation(row) && !row.optional);
};

/**
 * Calculates the effective discontinuation rate per visit when given a total rate
 * @param totalRate - The total/overall discontinuation rate (e.g. 0.5 for 50%)
 * @param totalVisits - Total number of non-optional visits
 * @returns The effective discontinuation rate per visit
 */
export const calculateEffectiveDiscontinuationRate = (
  totalRate: number,
  totalVisits: number
): number => {
  // Effective Visit Discontinuation Rate = 1 - (1 - Total Implied Rate)^(1 / N)
  return decimalDifference(
    1,
    Math.pow(decimalDifference(1, totalRate), decimalDivide(1, totalVisits))
  );
};

/**
 * Calculates cumulative discontinuation and retention rates for a series of visits
 * @param rows - Array of visit data with discontinuation rates
 * @param totalImpliedRate - Optional overall discontinuation rate. If provided, will be distributed evenly
 * @returns Updated array with cumulative values calculated
 */
export const calculateCumulativeValues = <T extends ProtocolEntryGridRow>(
  rows: T[],
  totalImpliedRate?: number
): T[] => {
  // Filter and sort non-optional visits
  const sortedRows = [...rows].sort((a, b) => {
    if (a.target_date_days_out !== undefined && b.target_date_days_out !== undefined) {
      return a.target_date_days_out - b.target_date_days_out;
    }

    return 0;
  });

  const totalNonOptionalVisits = rows.filter((row) => !row.optional).length;

  // Calculate effective rate if total rate is provided
  const effectiveRate =
    totalImpliedRate !== undefined
      ? calculateEffectiveDiscontinuationRate(totalImpliedRate, totalNonOptionalVisits)
      : undefined;

  let cumulativeRetention = 1;

  return sortedRows.map((row) => {
    if (Object.keys(row.errors || {}).length) {
      return row;
    }

    if (!canDiscontinuationExist(row)) {
      return {
        ...row,
        cumulative_discontinuation: null,
        cumulative_retention: null,
        discontinuation_rate: null,
        effective_discontinuation_rate: null,
      };
    }

    // Use effective rate if provided, otherwise use row's rate
    const discontinuationRate = effectiveRate ?? decimalDivide(row.discontinuation_rate || 0, 100);

    const retention = decimalDifference(1, discontinuationRate);

    cumulativeRetention = decimalMultiply(cumulativeRetention, retention);

    return {
      ...row,
      cumulative_discontinuation: decimalDifference(1, cumulativeRetention),
      cumulative_retention: cumulativeRetention,
      discontinuation_rate:
        row.discontinuation_rate === null
          ? null
          : decimalRoundingToNumber(row.discontinuation_rate ?? 0),
      effective_discontinuation_rate: decimalMultiply(effectiveRate || 0, 100),
    };
  });
};
